import {Outlet, useLocation, useNavigate} from 'react-router-dom'
import './Layout.css'
import BookIcon from '@/assets/icons/BookIcon'
import ToolIcon from '@/assets/icons/ToolIcon'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {getApiService, useApi} from '@/services/api'
import {RolesProvider} from '@/contexts/RolesContext'
import {Role} from '@/pages/Admin/Catalogs/Roles'
import { RootNavPanel, SideNav } from '@fs/uikit-react'

const AdminLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [arePanelsVisible, setArePanelsVisible] = useState<boolean>(true);

  // Navigate to first menu item and its first sub-item by default
  useEffect(() => {
    if (location.pathname === '/admin') {
      navigate('/admin/catalogs/users');
    }
  }, [location.pathname, navigate]);

  // Function to toggle visibility of all panels
  const handleToggleAllPanels = useCallback(() => {
    setArePanelsVisible(prevState => !prevState);
  }, []);

  // Add a keyboard shortcut (Alt+F) to toggle all panels
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && (event.code === 'KeyF')) {
        handleToggleAllPanels();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleToggleAllPanels]);

  // Get API service
  const apiService = useMemo(() => getApiService(), [])

  // Memoize the roles API call function
  const rolesApiCallFunction = useMemo(() =>
    (client: { get: (arg0: string) => any; }) => client.get('/roles'),
    []
  )

  // Fetch roles at the layout level
  const {
    data: roles,
    loading: rolesLoading,
    error: rolesError,
  } = useApi<Role[]>(
    apiService.client,
    rolesApiCallFunction,
    apiService.loadingStateManager,
    'roles-list'
  )

  const menuItems = [
    {
      id: 'catalogs',
      label: 'Каталоги',
      icon: <BookIcon />,
      active: location.pathname.includes('/admin/catalogs'),
      subItems: [
        {
          id: 'users',
          label: 'Користувачі',
          onClick: () => { navigate('/admin/catalogs/users'); },
          active: location.pathname === '/admin/catalogs/users'
        },
        {
          id: 'roles',
          label: 'Ролі',
          onClick: () => { navigate('/admin/catalogs/roles'); },
          active: location.pathname === '/admin/catalogs/roles'
        }
      ]
    },
    {
      id: 'tools',
      label: 'Засоби',
      icon: <ToolIcon />,
      onClick: () => { navigate('/admin/tools'); },
      active: location.pathname === '/admin/tools'
    }
  ]

  return (
    <RolesProvider roles={roles} loading={rolesLoading} error={rolesError}>
      <div className="layout admin-theme">
        <header className="header">
          <RootNavPanel
            labelText="Адміністратор"
            logoImage={'/logo.svg'}
            className={!arePanelsVisible ? 'fs-root-nav-panel--hidden' : ''}
          />
        </header>
        <div className="content-container">
          <SideNav 
            menuItems={menuItems} 
            className="admin-sidenav"
            collapsed={!arePanelsVisible}
          />
          <main className="main">
            <Outlet />
          </main>
        </div>
      </div>
    </RolesProvider>
  )
}

export default AdminLayout
